import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLd from "@/components/base/JsonLd";
import usePageMeta from "@/hooks/usePageMeta";
import { aboutPillars, testimonials, milestones, siteStats, categories } from "@/mocks/gossip";

export default function AboutPage() {
  usePageMeta("关于我们 - 品牌介绍与内容原则", {
    description:
      "了解91吃瓜网的定位、发展理念与内容原则：我们如何整理时间线、如何给材料分级，以及读者如何参与纠错与线索提供。",
    keywords: "91吃瓜网,关于我们,内容原则,时间线整理,信息溯源",
    canonicalPath: "/about",
    ogType: "website",
  });

  return (
    <>
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于91吃瓜网",
            description: "91吃瓜网的品牌介绍、网站定位、发展理念与内容原则",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "91吃瓜网",
            description: "以时间线、前因后果与关系图整理娱乐事件的信息门户",
            email: "contact@91chigua-daily.example",
          },
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系91吃瓜网",
            description: "内容纠错、线索提供与内容合作的联系方式",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://microtransport.cn/" },
              { "@type": "ListItem", position: 2, name: "关于我们" },
            ],
          },
        ]}
      />
      <SiteHeader />
      <main className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb items={[{ name: "首页", to: "/" }, { name: "关于我们" }]} />
        </div>

        <header className="w-full px-4 md:px-8 pt-8 pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.25em] text-primary-600 uppercase">
                <span className="w-6 h-px bg-primary-500"></span>
                关于我们
              </span>
              <h1 className="mt-3 font-heading font-black text-3xl md:text-[44px] leading-tight text-foreground-950">
                一个把瓜料排成时间线的地方
              </h1>
              <p className="mt-5 font-label text-base md:text-lg leading-relaxed text-foreground-600 max-w-2xl">
                91吃瓜网是一个以「深扒复盘」为核心的娱乐信息整理站点。我们不追首发，也不替读者下结论，只做一件事：把散落的信息按时间顺序排好，标清来源，让你自己看明白。
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/events"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 font-label text-sm text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-2-line"></i>
                  去看复盘档案
                </Link>
                <a
                  href="mailto:contact@91chigua-daily.example"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-6 py-3 font-label text-sm text-foreground-900 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-send-line"></i>
                  联系我们
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-lg border border-background-300 bg-background-200">
                <div className="w-full aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20editorial%20still%20life%20of%20stacked%20manila%20case%20folder%20archives%20with%20red%20string%20connections%20pinned%20on%20a%20cork%20board%20vintage%20desk%20lamp%20warm%20amber%20light%20deep%20rust%20and%20paper%20cream%20tones%20shallow%20depth%20of%20field%20documentary%20aesthetic%20film%20grain%20high%20detail&width=1200&height=900&seq=about-91chigua-2026&orientation=landscape"
                    alt="91吃瓜网档案整理桌面示意"
                    className="w-full h-full object-top object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          <dl className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-background-200 pt-8">
            {siteStats.map((s) => (
              <div key={s.label}>
                <dt className="font-heading font-black text-2xl md:text-3xl text-primary-600">{s.value}</dt>
                <dd className="mt-1 font-label text-sm text-foreground-500">{s.label}</dd>
              </div>
            ))}
          </dl>
        </header>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <h2 className="font-heading font-bold text-2xl md:text-[32px] text-foreground-950">
              定位、理念与原则
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutPillars.map((p) => (
                <article key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">{p.title}</h3>
                  <p className="mt-2 font-label text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </article>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8">
              <h3 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-shield-star-line text-primary-600"></i>
                内容可信度分级
              </h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 font-label text-sm">
                <div className="rounded-md bg-background-50 p-4">
                  <span className="inline-block rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] text-background-50">直接证据</span>
                  <p className="mt-2 text-foreground-700 leading-relaxed">官方文书、可交叉验证的时间记录等。</p>
                </div>
                <div className="rounded-md bg-background-50 p-4">
                  <span className="inline-block rounded-full bg-accent-500 px-2.5 py-0.5 text-[11px] text-accent-950">间接证据</span>
                  <p className="mt-2 text-foreground-700 leading-relaxed">需要结合其他信息才能成立的线索。</p>
                </div>
                <div className="rounded-md bg-background-50 p-4">
                  <span className="inline-block rounded-full bg-background-300 px-2.5 py-0.5 text-[11px] text-foreground-800">旁证</span>
                  <p className="mt-2 text-foreground-700 leading-relaxed">匿名爆料、模糊画面等仅作参考的材料。</p>
                </div>
                <div className="rounded-md bg-background-50 p-4">
                  <span className="inline-block rounded-full bg-secondary-300 px-2.5 py-0.5 text-[11px] text-secondary-950">待核实</span>
                  <p className="mt-2 text-foreground-700 leading-relaxed">无法溯源的部分，明确标出，不当成事实。</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.25em] text-primary-600 uppercase">
                  <span className="w-6 h-px bg-primary-500"></span>
                  发展历程
                </span>
                <h2 className="mt-3 font-heading font-bold text-2xl md:text-[32px] leading-tight text-foreground-950">
                  一路只想把话说清楚
                </h2>
                <p className="mt-3 font-label text-sm md:text-[15px] leading-relaxed text-foreground-600">
                  从最早的单篇复盘，到现在的可信度分级与公开修订，我们一直在打磨同一件事。
                </p>
              </div>
              <div className="lg:col-span-8">
                <ol className="relative border-l border-background-300 pl-6 md:pl-8 space-y-7">
                  {milestones.map((m, idx) => (
                    <li key={m.year} className="relative">
                      <span
                        className={`absolute -left-[31px] md:-left-[39px] top-1.5 w-3 h-3 rounded-full border-2 ${
                          idx === 0 ? "bg-primary-500 border-primary-300" : "bg-background-50 border-background-400"
                        }`}
                        aria-hidden="true"
                      ></span>
                      <time className="font-heading font-bold text-sm text-primary-700">{m.year}</time>
                      <h3 className="mt-1 font-heading font-semibold text-base md:text-lg text-foreground-950">
                        {m.title}
                      </h3>
                      <p className="mt-1 font-label text-sm leading-relaxed text-foreground-600">{m.desc}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <h2 className="font-heading font-bold text-2xl md:text-[32px] text-foreground-950">
              读者怎么说
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t) => (
                <figure key={t.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                  <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                  <blockquote className="mt-3 flex-1 font-label text-sm leading-relaxed text-foreground-700">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-4 border-t border-background-200 pt-3">
                    <span className="block font-heading font-semibold text-sm text-foreground-950">{t.name}</span>
                    <span className="font-label text-xs text-foreground-500">{t.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  联系我们
                </h2>
                <p className="mt-3 font-label text-sm md:text-[15px] leading-relaxed text-foreground-600">
                  无论是发现内容有误、想提供可核实的线索，还是内容合作与授权咨询，都欢迎来信。我们会在三个工作日内回复，被采纳的更正会同步更新到对应页面。
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href="mailto:contact@91chigua-daily.example"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 font-label text-sm text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-send-line"></i>
                    contact@91chigua-daily.example
                  </a>
                  <Link
                    to="/events"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-6 py-3 font-label text-sm text-foreground-900 hover:border-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    浏览复盘档案
                  </Link>
                </div>
                <p className="mt-5 font-label text-xs text-foreground-500 leading-relaxed">
                  说明：本站人物与事件均为示例内容，用于展示信息整理与时间线复盘的方式，不指向任何真实的个人或机构。
                </p>
              </div>

              <div className="lg:col-span-5 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                <h2 className="font-heading font-bold text-xl text-foreground-950">主题分区</h2>
                <div className="mt-4 flex flex-col gap-2">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="group flex items-center gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-800">
                        <i className={`${c.icon} text-base`}></i>
                      </span>
                      <span className="font-label text-sm text-foreground-800 group-hover:text-primary-700 transition-colors">
                        {c.name}
                      </span>
                      <i className="ri-arrow-right-line ml-auto text-foreground-400 group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}